// Media API configuration and helper functions
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });

const MEDIA_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  ENDPOINTS: {
    MEDIA_FILES: '/api/media/files/',
    MEDIA_SERVE: '/media/', // Django media serving endpoint
    MEDIA_DOWNLOAD: '/api/media/files/',
  }
};

// Helper function for API calls with authentication
const mediaApiCall = async (endpoint, options = {}) => {
  const token = ls.get('authToken');
  
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const defaultHeaders = {
    'Authorization': `Token ${token}`,
    ...options.headers
  };

  const config = {
    ...options,
    headers: defaultHeaders
  };

  try {
    const response = await fetch(`${MEDIA_API_CONFIG.BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Media API call failed:', error);
    throw error;
  }
};

// Media API functions
export const mediaAPI = {
  // Get all media files with optional filters
  getMediaFiles: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES}?${queryString}`;
    return mediaApiCall(endpoint);
  },

  // Get a specific media file by ID
  getMediaFile: (fileId) => {
    const endpoint = `${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES}${fileId}/`;
    return mediaApiCall(endpoint);
  },

  // Get media files by user
  getUserMedia: (userId, params = {}) => {
    const queryString = new URLSearchParams({ owner: userId, ...params }).toString();
    const endpoint = `${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES}?${queryString}`;
    return mediaApiCall(endpoint);
  },

  // Upload a new media file
  uploadMedia: (fileData) => {
    const formData = new FormData();
    
    // Required fields
    formData.append('file', fileData.file);
    formData.append('media_type', fileData.media_type);
    
    // Optional fields
    if (fileData.title) formData.append('title', fileData.title);
    if (fileData.description) formData.append('description', fileData.description);
    if (fileData.is_public !== undefined) formData.append('is_public', fileData.is_public);
    if (fileData.is_private !== undefined) formData.append('is_private', fileData.is_private);
    if (fileData.collection_id) formData.append('collection_id', fileData.collection_id);
    
    return mediaApiCall(MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData - let the browser set it automatically
      }
    });
  },

  // Update a media file
  updateMedia: (fileId, updateData) => {
    const endpoint = `${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES}${fileId}/`;
    return mediaApiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  // Delete a media file
  deleteMedia: (fileId) => {
    const endpoint = `${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_FILES}${fileId}/`;
    return mediaApiCall(endpoint, {
      method: 'DELETE'
    });
  }
};

// Helper function to construct media serve URLs for private images
export const getMediaServeUrl = (mediaFileId) => {
  const baseUrl = MEDIA_API_CONFIG.BASE_URL;
  return `${baseUrl}/api/media/files/${mediaFileId}/serve/`;
};

// Helper function to get media thumbnail URL (if available)
export const getMediaThumbnailUrl = (mediaFileId) => {
  const baseUrl = MEDIA_API_CONFIG.BASE_URL;
  return `${baseUrl}${MEDIA_API_CONFIG.ENDPOINTS.MEDIA_SERVE}${mediaFileId}/thumbnail/`;
};

export default mediaAPI; 